import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { WalkInServices } from './WalkInChoices';
import { useCompanyServices } from '@/hooks/queries/useCompanyServices';
import { useAuth } from '@/hooks/useAuth';
import { walkInQueryKeys } from '@/hooks/queries/walk-in.queries';
import { WalkInService } from '@/services/walk-in.service';
import { getApiErrorMessage } from '@/lib/api/error';
import type { ProfileWalkInDto } from '@/types/dto/walk-in.dto';

export function WalkInServiceSelection({
  entry,
  companyId,
  allowMultiple,
}: {
  entry: ProfileWalkInDto;
  companyId: string;
  allowMultiple: boolean;
}) {
  const { services, isLoading, isError, refetch } = useCompanyServices(companyId);
  const { user } = useAuth();
  const client = useQueryClient();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const selected = draft ?? entry.services?.map((service) => service.serviceId) ?? [];
  const save = async () => {
    if (!entry.id || saving) return;
    setSaving(true);
    setError('');
    try {
      const updated = await WalkInService.updateServices(entry.id, selected);
      client.setQueryData(walkInQueryKeys.active(user?.id), updated);
      setDraft(null);
      setDone(true);
      void client.invalidateQueries({ queryKey: walkInQueryKeys.all });
    } catch (cause) {
      setError(getApiErrorMessage(cause, 'Não foi possível salvar os serviços. Seu lugar na fila continua garantido.'));
    } finally {
      setSaving(false);
    }
  };
  return (
    <details
      className="bg-card border-border space-y-4 rounded-2xl border p-5 text-left"
      aria-label="Serviços opcionais"
    >
      <summary className="focus-visible:ring-primary min-h-11 cursor-pointer rounded-lg py-3 text-sm font-semibold focus-visible:ring-2">
        Escolher serviços <span className="text-muted-foreground font-normal">· Opcional</span>
      </summary>
      <p className="text-muted-foreground text-sm">
        Adiante seu atendimento ou decida no local. Isso não muda sua posição na fila.
      </p>
      {done ? (
        <div className="space-y-3">
          <p role="status">
            {entry.services?.length
              ? 'Serviços informados! Agora é só aguardar sua vez.'
              : 'Tudo certo! Você pode decidir os serviços no local.'}
          </p>
          <Button variant="outline" onClick={() => setDone(false)}>
            Alterar serviços
          </Button>
        </div>
      ) : (
        <>
          {isLoading ? (
            <p role="status">Carregando serviços…</p>
          ) : isError ? (
            <div role="alert">
              <p>Não foi possível carregar os serviços. Seu lugar está garantido.</p>
              <Button variant="link" onClick={() => void refetch()}>
                Tentar novamente
              </Button>
            </div>
          ) : (
            <fieldset disabled={saving}>
              <WalkInServices
                services={services}
                selectedIds={new Set(selected)}
                onToggle={(id) =>
                  setDraft(
                    selected.includes(id)
                      ? selected.filter((item) => item !== id)
                      : allowMultiple
                        ? [...selected, id]
                        : [id],
                  )
                }
              />
            </fieldset>
          )}
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <div className="flex flex-col gap-2 sm:flex-row">
            <Button onClick={() => void save()} disabled={saving || isLoading || isError || !services.length}>
              {saving ? 'Salvando…' : 'Salvar serviços'}
            </Button>
            <Button
              variant="ghost"
              disabled={saving}
              onClick={() => {
                setDraft(null);
                setDone(true);
              }}
            >
              Decidir no local
            </Button>
          </div>
        </>
      )}
    </details>
  );
}
